import React, {
  createContext,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { auth } from '@/core/auth/firebase';
import { AuthService } from '@/core/auth/AuthService';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { usePages } from '@/features/pages/context/PagesContext';
import { useTags } from '@/features/tags/context/TagsContext';
import { useCollections } from '@/features/collections/context/CollectionsContext';
import { useSyncStore } from '@/features/sync/stores/useSyncStore';
import { SyncProgress } from '@/features/sync/types/sync';
import syncRepository from '../repositories/SyncRepository';
import { SyncEngine } from '@/features/sync/services/SyncEngine';

interface SyncContextType {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  handleSync: () => void;
  syncProgress: SyncProgress | null;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

interface SyncProviderProps {
  children: ReactNode;
  syncInterval?: number;
}

export const SyncProvider: React.FC<SyncProviderProps> = ({ children }) => {
  // Get state and actions from Zustand store
  const {
    user: currentUser,
    setSyncState,
    setUser,
    setAuthLoading,
    setLastSyncedAt,
    syncLoading,
  } = useSyncStore();

  // Add state for tracking sync progress
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);

  const setIsDirty = usePageStore(state => state.setIsDirty);
  const { loadPages, refreshCurrentPage } = usePages();
  const { loadCollections } = useCollections();
  const { refreshTags } = useTags();

  // Services initialization
  const authService = useMemo(() => new AuthService(), []);
  const syncEngine = useMemo(() => new SyncEngine(), []);

  // Sync core with better error handling and retry logic
  const syncCore = useCallback(
    async (showProgress = false) => {
      if (syncLoading) return;

      try {
        // Start sync process
        setSyncState({ syncLoading: true, syncStatus: 'syncing' });

        // Set initial progress state regardless of showProgress flag
        const initialProgress: SyncProgress = {
          pagesProcessed: 0,
          totalEstimatedPages: 0,
          currentBatch: 0,
          totalBatches: null,
          status: 'collections',
          collectionsProcessed: 0,
          totalEstimatedCollections: 0,
          currentCollectionBatch: 0,
          totalCollectionBatches: null,
          latestProcessedTimestamp: null,
          pagesCompletionPercentage: 0,
          collectionsCompletionPercentage: 0, // Start at 5% to show initial progress
          overallCompletionPercentage: 1,
          currentEntityBeingProcessed: 'Starting sync...',
        };
        if (showProgress) setSyncProgress(initialProgress);

        // Perform sync directly in the main thread
        await syncEngine.performSync({
          // Progress updates
          onProgress: progress => {
            console.log('Progress update:', progress, { showProgress });
            // Always show progress during sync to ensure UI updates
            if (showProgress) setSyncProgress(progress);

            // Log progress with more detail for debugging
            console.log(
              `Sync Progress: ${progress.status} - Overall: ${String(progress.overallCompletionPercentage)}%, ` +
                `Collections: ${String(progress.collectionsCompletionPercentage)}%, ` +
                `Pages: ${String(progress.pagesCompletionPercentage)}%, ` +
                `Current: ${progress.currentEntityBeingProcessed || ''}`
            );
          },

          // Collection sync completion - trigger UI update for collections
          onCollectionSyncComplete: () => {
            console.log('Collections synced, updating UI');
            // Use void to avoid waiting for this promise, as it's just a UI update
            void loadCollections();
          },
          // Collection batch completion - trigger UI update after each batch
          onCollectionBatchSyncComplete: (batchNumber, totalBatches) => {
            console.log(
              `Collection batch ${String(batchNumber)} of ${totalBatches ? String(totalBatches) : '?'} synced, updating UI`
            );
            // Use void to avoid waiting for this promise, as it's just a UI update
            void loadCollections();
          },

          // Page batch completion - trigger UI update after each batch
          onPageBatchSyncComplete: (batchNumber, totalBatches) => {
            console.log(
              `Page batch ${String(batchNumber)} of ${totalBatches ? String(totalBatches) : '?'} synced, updating UI`
            );
            // Use void to avoid waiting for this promise, as it's just a UI update
            void loadPages();
          },

          // Final completion
          onComplete: async (lastSynced, updatedPageIds) => {
            setIsDirty(false);

            // Final refresh - we want to wait for these since they're important for the final state
            if (updatedPageIds?.length) {
              await loadPages();
              await refreshTags();
            }
            await loadCollections();

            console.log('Sync completed successfully');
            const pageId = window.location.pathname.split('page/').pop();
            console.log('Page ID:', pageId);
            console.log('Updated Page IDs:', updatedPageIds);
            if (updatedPageIds?.length && pageId && updatedPageIds.includes(pageId)) {
              void refreshCurrentPage();
            }

            setSyncState({
              syncLoading: false,
              error: null,
              lastSyncedAt: lastSynced,
              syncStatus: 'success',
            });
          },

          // Offline handler
          onOffline: (error = 'Offline') => {
            setSyncState({
              syncLoading: false,
              error: new Error(error),
              syncStatus: 'error',
            });
            setSyncProgress(null);
          },
        });
      } catch (err) {
        console.error('Sync error:', err);
        setSyncState({
          syncLoading: false,
          error: err instanceof Error ? err : new Error('Sync failed'),
          syncStatus: 'error',
        });
        setSyncProgress(null);
      }
    },
    [
      syncLoading,
      syncEngine,
      setSyncState,
      setSyncProgress,
      loadCollections,
      loadPages,
      refreshTags,
      refreshCurrentPage,
      setIsDirty,
    ]
  );

  const handleSync = useCallback(() => {
    if (syncLoading || !auth.currentUser) return;

    const runSync = async () => {
      const lastSynced = await syncRepository.getLastSyncTime();
      const showProgress = !lastSynced;

      console.log(`Running sync with showProgress: ${showProgress ? 'visible' : 'hidden'}`);
      await syncCore(showProgress);
    };
    void runSync();
  }, [syncLoading, syncCore]);

  // Auth state management
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(user => {
      setUser(user);
      setAuthLoading(false);

      if (user?.uid && currentUser?.uid !== auth.currentUser?.uid) {
        // Delay sync to avoid race conditions with other processes. Page list seems to load slower if this is running
        setTimeout(() => {
          handleSync();
        }, 5000);
      } else {
        void syncRepository.getLastSyncTime().then(lastSyncTime => {
          setLastSyncedAt(lastSyncTime);
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [authService, currentUser?.uid, handleSync, setAuthLoading, setLastSyncedAt, setUser]);

  // Public methods

  const signIn = useCallback(async () => {
    try {
      setSyncState({ authLoading: true, error: null });
      await authService.signIn();
    } catch (err) {
      setSyncState({
        error: err instanceof Error ? err : new Error('Sign in failed'),
        authLoading: false,
      });
      throw err;
    }
  }, [authService, setSyncState]);

  const signOut = useCallback(async () => {
    try {
      setSyncState({ error: null });
      await authService.signOut();
      setLastSyncedAt(null);
    } catch (err) {
      setSyncState({
        error: err instanceof Error ? err : new Error('Sign out failed'),
      });
      throw err;
    }
  }, [authService, setSyncState, setLastSyncedAt]);

  const value = useMemo(
    () => ({
      signIn,
      signOut,
      handleSync,
      syncProgress,
    }),
    [signIn, signOut, handleSync, syncProgress]
  );

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
};

export const useSyncContext = () => {
  const context = useContext(SyncContext);
  if (context === undefined) {
    throw new Error('useSyncContext must be used within a SyncProvider');
  }

  // Only return methods, not state
  return context;
};
